import {BaseQueryFn, createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {FetchArgs, FetchBaseQueryError} from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import {Modal} from "antd";
import _ from "lodash";
import LogRocket from "logrocket";
import {defaultRedirectUrl} from "../../utils/const";

const serverError = _.debounce((title: string, content: string) => Modal.error({
    title,
    content
}), 100);

const authError = _.debounce((title: string, content: string) => Modal.error({
    title,
    content
}), 100);


const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    credentials: 'include',
    prepareHeaders: (headers) => headers
});

// Mutex per evitare chiamate refresh concorrenti
const mutex = {
    locked: false,
    waitQueue: [] as Array<() => void>,
    refreshFailed: false // Flag per indicare che il refresh è fallito
};

const acquire = () => new Promise<void>((resolve) => {
    if (!mutex.locked) {
        mutex.locked = true;
        resolve();
    } else {
        mutex.waitQueue.push(resolve);
    }
});

const release = () => {
    const next = mutex.waitQueue.shift();
    if (next) {
        next();
    } else {
        mutex.locked = false;
    }
};

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    // Se il refresh è già fallito, aborta immediatamente questa chiamata
    if (mutex.refreshFailed) {
        return {
            error: {
                status: 401,
                data: { message: "Session expired" }
            }
        } as { error: FetchBaseQueryError };
    }

    // Aspetta il turno se c'è un refresh in corso
    await acquire();

    try {
        // Controlla di nuovo dopo aver acquisito il lock
        if (mutex.refreshFailed) {
            return {
                error: {
                    status: 401,
                    data: { message: "Session expired" }
                }
            } as { error: FetchBaseQueryError };
        }

        let result = await baseQuery(args, api, extraOptions);

        if (result?.error?.status === 401 && !["loginWithCookies", "refreshCookies"].includes(api.endpoint)) {
            // Prova a fare refresh dei cookies
            const refreshResult = await baseQuery({
                url: '/auth/refresh/',
                method: 'POST'
            }, api, extraOptions);

            if (refreshResult?.data) {
                // Se il refresh ha successo, riprova la richiesta originale
                result = await baseQuery(args, api, extraOptions);
            } else {
                // Segnala che il refresh è fallito per bloccare le altre chiamate
                mutex.refreshFailed = true;

                // Mostra il modal solo per la prima chiamata che fallisce
                const modal = Modal.warning({
                    title: "Sessione scaduta",
                    content: "Verrai reindirizzato alla pagina di login...",
                    onOk: () => {
                        modal.destroy();
                        window.location.replace(defaultRedirectUrl)
                    }
                });
                setTimeout(() => {
                    modal.destroy();
                    window.location.replace(defaultRedirectUrl)
                }, 2000);
            }
        }

        if (result?.error?.status === "PARSING_ERROR" || result?.error?.status === "FETCH_ERROR" || result?.error?.status === 500 || result?.error?.status === 0) {
            serverError("Errore nel caricamento dei dati", "Se il problema persiste contattaci all' indirizzo support@riskapp.it");
            LogRocket.captureMessage(result?.error?.status.toString());
            console.error(result);
        }

        if (result?.error?.status === 403) {
            authError("Autorizzazione negata", "Non hai il permesso per effettuare questa operazione");
        }

        return result;
    } finally {
        // Rilascia il lock permettendo alla prossima chiamata di procedere
        release();
    }
};

export const apiSlice = createApi({
    baseQuery: baseQueryWithReauth,
    endpoints: builder => ({}),
    tagTypes: [
        "AdditionalInsureds",
        "Agreements",
        "AgreementRows",
        "BankBranches",
        "Brokers",
        "MeetingNotes",
        "StateTransitions",
        "CoverageConfirmation",
        "CoverageConfirmationData",
        "CoverageConfirmationSchema",
        "Negotiation",
        "NegotiationToAlignWithinCoverageConfirmation",
        "NegotiationLps",
        "AccountingStatements",
        "StatementTitles",
        "EmittedTitlesDocuments",
        "EmittedTitles",
        "Remittances",
        "ReconciliationGroups",
        "StatementTitlesGroups",
        "ExternalDataReports",
    ]
});